/**
 * SolarComparisonTab Component
 *
 * Fourth Energy tab: Fixed vs Agile vs Agile+Solar (optional +Battery).
 * Headline savings tiles, simulation metadata, chart, collapsible solar
 * parameters panel (with optional battery layer), and monthly table.
 *
 * No-export model: PV beyond load (and optional battery) is curtailed.
 */

import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useSolarSimulation,
  DEFAULT_SOLAR,
} from '@/hooks/useSolarSimulation'
import {
  identifySolarPreset,
  getSolarPreset,
  presetMidPriceAt,
  CUSTOM_SOLAR_PRESET_ID,
  ratedKwp,
} from '@/lib/solarPresets'
import {
  identifyPreset as identifyBatteryPreset,
  getPreset as getBatteryPreset,
  presetMidPrice as batteryMidPrice,
  CUSTOM_PRESET_ID as CUSTOM_BATTERY_PRESET_ID,
} from '@/lib/batteryPresets'
import SolarComparisonChart from './SolarComparisonChart'
import SolarParametersPanel from './SolarParametersPanel'

function formatGbp(pence, vatRate = 0) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format((pence / 100) * (1 + vatRate))
}

function formatGbp2(pence, vatRate = 0) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format((pence / 100) * (1 + vatRate))
}

function formatMonth(monthStr) {
  return new Date(monthStr).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function formatRelative(iso) {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diffMin = Math.round((now - then) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? '' : 's'} ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? '' : 's'} ago`
  const diffDay = Math.round(diffHr / 24)
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
}

function annualise(pence, daysInWindow) {
  if (!daysInWindow) return 0
  return (pence / daysInWindow) * 365
}

/**
 * A single scenario column: headline annual bill, £/yr saved vs the
 * current (Fixed) baseline, and — for options that require capex — the
 * total installed cost + simple payback years.
 */
function ScenarioColumn({ title, annualPence, savingPence, capexGbp, paybackYears, isBaseline, vatRate }) {
  return (
    <div className="flex flex-col gap-1 p-4">
      <p className="text-xs text-neutral-500 uppercase tracking-wide">{title}</p>
      <p className="text-2xl font-bold text-neutral-900 mt-1">
        {formatGbp(annualPence, vatRate)}<span className="text-sm font-normal text-neutral-500">/yr</span>
      </p>
      {isBaseline ? (
        <p className="text-xs text-neutral-500">current baseline</p>
      ) : (
        <p className="text-xs text-green-600 font-medium">
          saves {formatGbp(savingPence, vatRate)}/yr
        </p>
      )}
      {capexGbp != null ? (
        <p className="text-xs text-neutral-500 mt-1">
          £{(capexGbp / 1000).toFixed(1)}k installed
          {paybackYears != null && <> · {paybackYears.toFixed(1)} yrs payback</>}
        </p>
      ) : isBaseline ? (
        <p className="text-xs text-neutral-400 mt-1">—</p>
      ) : (
        <p className="text-xs text-neutral-500 mt-1">no capex</p>
      )}
    </div>
  )
}

function ScenarioComparison({ data, isLoading, vatRate = 0, solarPreset, batteryPreset, solar }) {
  if (isLoading) {
    return <Skeleton className="h-44 w-full" />
  }
  if (!data?.aggregate) return null

  const {
    total_fixed_pence,
    total_agile_pence,
    total_agile_solar_pence,
    total_agile_solar_battery_pence,
  } = data.aggregate
  const monthly = data.monthly ?? []
  const daysInWindow = monthly.reduce((s, m) => s + (m.days_in_window ?? 0), 0)
  if (!daysInWindow) return null

  const hasBattery = total_agile_solar_battery_pence != null

  // Annualise each scenario's windowed total so all figures are
  // comparable as "£/yr at this site's load".
  const annualFixed = annualise(total_fixed_pence, daysInWindow)
  const annualAgile = annualise(total_agile_pence, daysInWindow)
  const annualSolar = annualise(total_agile_solar_pence, daysInWindow)
  const annualCombo = hasBattery
    ? annualise(total_agile_solar_battery_pence, daysInWindow)
    : null

  // All savings are measured against Fixed (the status quo).
  const savingAgile = annualFixed - annualAgile
  const savingSolar = annualFixed - annualSolar
  const savingCombo = annualCombo != null ? annualFixed - annualCombo : null

  // Capex (hardware + install midpoints). Null when the user has
  // edited params off a preset — we can't price a custom config.
  const solarCapex = presetMidPriceAt(solarPreset, solar?.num_panels ?? 40)
  const batteryCapex = hasBattery ? batteryMidPrice(batteryPreset) : null
  const comboCapex = (solarCapex != null && batteryCapex != null)
    ? solarCapex + batteryCapex
    : null

  // Simple payback = total capex ÷ annual saving vs Fixed (inc VAT).
  const paybackFromCapex = (capex, savingPence) => {
    if (capex == null || !(savingPence > 0)) return null
    const savingGbpIncVat = (savingPence / 100) * (1 + vatRate)
    return capex / savingGbpIncVat
  }
  const paybackSolar = paybackFromCapex(solarCapex, savingSolar)
  const paybackCombo = paybackFromCapex(comboCapex, savingCombo)

  // Footer meta (previously SimulationMeta).
  const totalMatched = monthly.reduce((s, m) => s + (m.slots_matched ?? 0), 0)
  const totalExpected = monthly.reduce((s, m) => s + (m.slots_expected ?? 0), 0)
  const coverage = totalExpected ? (100 * totalMatched / totalExpected) : 0
  const genTotal = data.aggregate.solar_kwh_generated_total ?? 0
  const selfTotal = data.aggregate.solar_kwh_self_consumed_total ?? 0
  const selfPct = genTotal > 0 ? (100 * selfTotal / genTotal) : 0
  const annualGen = (genTotal / daysInWindow) * 365
  const cycles = data.aggregate.battery_full_cycles
  const annualCycles = cycles != null
    ? Math.round((cycles / daysInWindow) * 365)
    : null
  const kwp = solar ? ratedKwp(solar.panel_watts, solar.num_panels) : null
  const capacityFactor = data.aggregate.capacity_factor

  return (
    <Card>
      <CardContent className="p-0">
        <div className={`grid grid-cols-1 ${hasBattery ? 'md:grid-cols-4' : 'md:grid-cols-3'} divide-y md:divide-y-0 md:divide-x divide-neutral-200`}>
          <ScenarioColumn
            title="Current (Fixed)"
            annualPence={annualFixed}
            isBaseline
            vatRate={vatRate}
          />
          <ScenarioColumn
            title="Switch to Agile"
            annualPence={annualAgile}
            savingPence={savingAgile}
            vatRate={vatRate}
          />
          <ScenarioColumn
            title="+ Solar"
            annualPence={annualSolar}
            savingPence={savingSolar}
            capexGbp={solarCapex}
            paybackYears={paybackSolar}
            vatRate={vatRate}
          />
          {hasBattery && (
            <ScenarioColumn
              title="+ Solar + Battery"
              annualPence={annualCombo}
              savingPence={savingCombo}
              capexGbp={comboCapex}
              paybackYears={paybackCombo}
              vatRate={vatRate}
            />
          )}
        </div>
        <div className="border-t border-neutral-200 px-4 py-3">
          <p className="text-xs text-neutral-500">
            {coverage.toFixed(1)}% coverage over {monthly.length} months ·{' '}
            {kwp != null && <>{kwp.toFixed(1)} kWp · </>}
            {annualGen.toFixed(0)} kWh/yr generated ({selfPct.toFixed(0)}% self-consumed)
            {capacityFactor != null && <> · {(capacityFactor * 100).toFixed(1)}% cap. factor</>}
            {annualCycles != null && <> · {annualCycles} battery cycles/yr</>}
            {' · '}
            {data.cached ? 'cached' : 'computed'} {formatRelative(data.computed_at)}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

function MonthlyTable({ data, isLoading, vatRate = 0 }) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        </CardContent>
      </Card>
    )
  }
  const monthly = data?.monthly ?? []
  if (!monthly.length) return null

  const hasBattery = monthly.some(m => m.agile_solar_battery_total_pence != null)
  const sorted = [...monthly].reverse()
  const totals = monthly.reduce((acc, m) => ({
    kwh: acc.kwh + (m.kwh_consumed ?? 0),
    pv_gen: acc.pv_gen + (m.solar_kwh_generated ?? 0),
    pv_self: acc.pv_self + (m.solar_kwh_self_consumed ?? 0),
    fixed: acc.fixed + (m.fixed_total_pence ?? 0),
    agile: acc.agile + (m.agile_total_pence ?? 0),
    solar: acc.solar + (m.agile_solar_total_pence ?? 0),
    combo: acc.combo + (m.agile_solar_battery_total_pence ?? 0),
    saving: acc.saving + (m.saving_vs_agile_pence ?? 0),
  }), { kwh: 0, pv_gen: 0, pv_self: 0, fixed: 0, agile: 0, solar: 0, combo: 0, saving: 0 })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 px-2 font-medium text-neutral-600">Month</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Load (kWh)</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">PV gen (kWh)</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Self-cons (kWh)</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Fixed</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Agile</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Agile + Solar</th>
                {hasBattery && (
                  <th className="text-right py-3 px-2 font-medium text-neutral-600">+ Battery</th>
                )}
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Saving vs Agile</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(m => (
                <tr key={m.month} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="py-2.5 px-2 font-medium text-neutral-900">{formatMonth(m.month)}</td>
                  <td className="py-2.5 px-2 text-right text-neutral-700">
                    {(m.kwh_consumed ?? 0).toFixed(0)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-neutral-700">
                    {(m.solar_kwh_generated ?? 0).toFixed(0)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-neutral-700">
                    {(m.solar_kwh_self_consumed ?? 0).toFixed(0)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-neutral-700">
                    {formatGbp2(m.fixed_total_pence ?? 0, vatRate)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-neutral-700">
                    {formatGbp2(m.agile_total_pence ?? 0, vatRate)}
                  </td>
                  <td className="py-2.5 px-2 text-right font-medium text-neutral-900">
                    {formatGbp2(m.agile_solar_total_pence ?? 0, vatRate)}
                  </td>
                  {hasBattery && (
                    <td className="py-2.5 px-2 text-right font-medium text-neutral-900">
                      {m.agile_solar_battery_total_pence != null
                        ? formatGbp2(m.agile_solar_battery_total_pence, vatRate)
                        : '—'}
                    </td>
                  )}
                  <td className="py-2.5 px-2 text-right text-green-600 font-medium">
                    {formatGbp2(m.saving_vs_agile_pence ?? 0, vatRate)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-neutral-300">
                <td className="py-3 px-2 font-bold text-neutral-900">Total</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{totals.kwh.toFixed(0)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{totals.pv_gen.toFixed(0)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{totals.pv_self.toFixed(0)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbp2(totals.fixed, vatRate)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbp2(totals.agile, vatRate)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbp2(totals.solar, vatRate)}</td>
                {hasBattery && (
                  <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbp2(totals.combo, vatRate)}</td>
                )}
                <td className="py-3 px-2 text-right font-bold text-green-600">{formatGbp2(totals.saving, vatRate)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

export default function SolarComparisonTab({ vatRate = 0, tariff, onTariffChange }) {
  const [solar, setSolar] = useState(DEFAULT_SOLAR)
  const [battery, setBattery] = useState(null)

  const { data, isLoading, error } = useSolarSimulation({ solar, battery })

  const appliedSolarPreset = useMemo(() => {
    const id = identifySolarPreset(solar)
    return id === CUSTOM_SOLAR_PRESET_ID ? null : getSolarPreset(id)
  }, [solar])

  const appliedBatteryPreset = useMemo(() => {
    if (!battery) return null
    const id = identifyBatteryPreset(battery)
    return id === CUSTOM_BATTERY_PRESET_ID ? null : getBatteryPreset(id)
  }, [battery])

  const handleApply = ({ solar: nextSolar, battery: nextBattery }) => {
    setSolar(nextSolar)
    setBattery(nextBattery)
  }

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-800">Failed to run solar simulation: {error}</p>
          </CardContent>
        </Card>
      )}

      <ScenarioComparison
        data={data}
        isLoading={isLoading}
        vatRate={vatRate}
        solarPreset={appliedSolarPreset}
        batteryPreset={appliedBatteryPreset}
        solar={solar}
      />

      <SolarComparisonChart
        data={data}
        isLoading={isLoading}
        vatRate={vatRate}
        tariff={tariff}
        onTariffChange={onTariffChange}
      />

      <SolarParametersPanel
        solar={solar}
        battery={battery}
        onApply={handleApply}
        isLoading={isLoading}
      />

      <MonthlyTable data={data} isLoading={isLoading} vatRate={vatRate} />
    </div>
  )
}
